/**
 * <holster-fit-finder> — "Find your fit" tool for the Mainstay IWB Holster.
 *
 * The customer picks their Gun, then Optic (none / red dot) and Light
 * (none / light or laser). Using the curated dataset (server-rendered into the
 * <script data-hff-data> JSON block by holster-fit-finder.liquid), it resolves
 * the correct holster Size code and selects the matching radio in Horizon's
 * native <variant-picker> (firing `change` so the theme updates price /
 * availability / add-to-cart). The native "Size" fieldset is hidden; the
 * "Magazine Pouch" option is left untouched.
 *
 * Dataset entry shape: { gun, base, optic, light, optic_light } where each size
 * field is a size code (e.g. "5C", "7-RDO") or null when that combination isn't
 * offered for that gun.
 */
class HolsterFitFinder extends HTMLElement {
  connectedCallback() {
    this.data = this.#loadData();
    this.gunSel = this.querySelector('[data-hff-gun]');
    this.opticSel = this.querySelector('[data-hff-optic]');
    this.lightSel = this.querySelector('[data-hff-light]');
    this.result = this.querySelector('[data-hff-result]');
    if (!this.data || !this.gunSel) return;

    this.#populateGuns();
    this.#hideNativeSize();
    this.#lock(true); // gate checkout until a gun is chosen

    this.form = document.querySelector('product-form-component form');
    this.form?.addEventListener('submit', this.#onSubmit, true);

    this.gunSel.addEventListener('change', () => this.#onGunChange());
    this.opticSel?.addEventListener('change', () => this.#compute());
    this.lightSel?.addEventListener('change', () => this.#compute());
  }

  /** Toggle the checkout lock. CSS (in the snippet) dims + blocks the buy
   *  buttons while <html> carries .hff-needs-gun; this class survives Horizon's
   *  buy-button morphs and also covers the sticky add-to-cart. */
  #lock(locked) {
    document.documentElement.classList.toggle('hff-needs-gun', locked);
  }

  /** Bulletproof guard: block the add-to-cart form submit (click OR keyboard)
   *  while locked, before Horizon's own handler runs. */
  #onSubmit = (e) => {
    if (document.documentElement.classList.contains('hff-needs-gun')) {
      e.preventDefault();
      e.stopImmediatePropagation();
      this.#say('Select your handgun above so we can set your size before adding to cart.', true);
      this.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  };

  #loadData() {
    try {
      return JSON.parse(this.querySelector('script[data-hff-data]').textContent);
    } catch {
      return null;
    }
  }

  #populateGuns() {
    const guns = this.data.guns.slice().sort((a, b) => a.gun.localeCompare(b.gun));
    const frag = document.createDocumentFragment();
    for (const g of guns) {
      const o = document.createElement('option');
      o.value = g.gun;
      o.textContent = g.gun;
      frag.appendChild(o);
    }
    this.gunSel.appendChild(frag);
  }

  #entry() {
    return this.data.guns.find((g) => g.gun === this.gunSel.value) || null;
  }

  #onGunChange() {
    const e = this.#entry();
    // Only offer Optic / Light when the chosen gun actually has those sizes.
    this.#toggleYes(this.opticSel, !!(e && (e.optic || e.optic_light)));
    this.#toggleYes(this.lightSel, !!(e && (e.light || e.optic_light)));
    this.#compute();
  }

  #toggleYes(sel, enabled) {
    if (!sel) return;
    const yes = sel.querySelector('option[value="yes"]');
    if (yes) yes.disabled = !enabled;
    if (!enabled && sel.value === 'yes') sel.value = 'no';
    sel.closest('[data-hff-field]')?.classList.toggle('hff__field--off', !enabled);
  }

  #compute() {
    const e = this.#entry();
    if (!e) {
      this.#lock(true);
      return this.#say('', false);
    }

    const optic = this.opticSel?.value === 'yes';
    const light = this.lightSel?.value === 'yes';

    let size = e.base;
    if (optic && light) size = e.optic_light;
    else if (optic) size = e.optic;
    else if (light) size = e.light;

    if (!size) {
      this.#lock(true);
      return this.#say(
        `We don't list a size for the <strong>${this.#esc(e.gun)}</strong> in that exact setup. ` +
          `Try changing the optic/light, or compare your gun on ` +
          `<a href="https://www.handgunhero.com/" target="_blank" rel="noopener">HandgunHero</a> and pick the matching size.`,
        true
      );
    }

    const ok = this.#selectSize(size);
    if (ok) {
      this.#lock(false);
      const extras = [optic ? 'optic' : null, light ? 'light/laser' : null].filter(Boolean).join(' + ');
      this.#say(
        `Recommended size <strong>${this.#esc(size)}</strong> — fits the ${this.#esc(e.gun)}` +
          `${extras ? ` with ${extras}` : ''}. We've set your size automatically.`,
        false
      );
    } else {
      this.#lock(true);
      this.#say(`Size ${this.#esc(size)} isn't available right now — please pick manually or contact us.`, true);
    }
  }

  #sizeFieldset() {
    return [...document.querySelectorAll('.variant-option, fieldset')].find(
      (f) => (f.querySelector('legend')?.textContent || '').trim().toLowerCase() === 'size'
    );
  }

  #hideNativeSize() {
    const fs = this.#sizeFieldset();
    if (fs) fs.style.display = 'none';
  }

  #selectSize(size) {
    const fs = this.#sizeFieldset();
    if (!fs) return false;
    const radio = fs.querySelector(`input[type="radio"][value="${CSS.escape(size)}"]`);
    if (!radio) return false;
    if (!radio.checked) {
      radio.checked = true;
      radio.dispatchEvent(new Event('input', { bubbles: true }));
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }
    return true;
  }

  #say(html, warn) {
    if (!this.result) return;
    this.result.innerHTML = html;
    this.result.hidden = !html;
    this.result.classList.toggle('hff__result--warn', !!warn);
  }

  #esc(v) {
    return String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }
}

if (!customElements.get('holster-fit-finder')) {
  customElements.define('holster-fit-finder', HolsterFitFinder);
}
